import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import apiService from "@/services/api/api.service";
import { useScreener } from "../../contexts/notification/ScreenerContext";
import { ArrowRight, TrendingUp, Calendar, Gift, ChevronRight, AlertCircle } from "lucide-react";

// Image placeholders (original URLs kept)
const blogImage =
  "https://media.gettyimages.com/id/1049277550/photo/retro-blog-bulb-sign-with-leather-armchair.jpg?s=612x612&w=0&k=20&c=EzRprmTVPOk5bONifiiBrq1S6j7VgqWY1sc8tKVbm4A=";
const expertsImage =
  "https://images.unsplash.com/photo-1551836022-d5d88e9218df?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&h=150&q=80";
const referImage =
  "https://lirp.cdn-website.com/0ff6c1a1/dms3rep/multi/opt/Rewards-0cce04c5-1920w.png";
const popularCompaniesImage =
  "https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&h=150&q=80";
const newlyAddedCompaniesImage =
  "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&h=150&q=80";
const dividendYieldImage =
  "https://m.foolcdn.com/media/dubs/images/Coins_with_Dice_on_them_-_spelling_out_yield_-.width-600.jpg";

// Reusable Card Component
const DashboardCard = ({ title, subtitle, image, badge, badgeColor, children, footerAction }) => (
  <div className="card-modern h-full flex flex-col relative overflow-hidden group border-t-4 border-t-transparent hover:border-t-[#44D2F6] transition-all duration-300">
    <div className="flex items-start justify-between mb-4">
      <div className="flex-1">
        {badge && (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mb-2 ${badgeColor}`}>
            {badge}
          </span>
        )}
        <h3 className="text-lg font-bold text-gray-900 group-hover:text-[#0C7FA5] transition-colors">
          {title}
        </h3>
        {subtitle && <p className="text-gray-500 text-xs mt-1">{subtitle}</p>}
      </div>
      <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 shadow-sm group-hover:shadow-md transition-shadow">
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
        />
      </div>
    </div>

    <div className="flex-1">
      {children}
    </div>

    {footerAction && (
      <div className="mt-4 pt-3 border-t border-gray-100">
        {footerAction}
      </div>
    )}
  </div>
);

// Blog Card Component
const BlogCard = ({
  title = "Latest Market Insights",
  image = blogImage,
  link = "/blog",
}) => {
  return (
    <DashboardCard
      title={title}
      subtitle="Stay updated with trends"
      image={image}
      badge="Featured"
      badgeColor="bg-blue-50 text-blue-700 border border-blue-100"
      footerAction={
        <Link
          to={link}
          className="flex items-center text-sm font-medium text-[#0C7FA5] hover:text-[#102147] transition-colors group/link"
        >
          Read Article <ArrowRight className="w-4 h-4 ml-1 transform group-hover/link:translate-x-1 transition-transform" strokeWidth={1.5} />
        </Link>
      }
    >
      <p className="text-gray-600 text-xs leading-relaxed line-clamp-3">
        Discover expert analysis and market trends that can help shape your investment strategy for the coming year.
      </p>
    </DashboardCard>
  );
};

// Experts Card Component
const ExpertsCard = ({
  name = "Financial Advisors",
  image = expertsImage,
  link = "/appointments",
}) => {
  return (
    <DashboardCard
      title={name}
      subtitle="Professional Guidance"
      image={image}
      badge="Expert Advice"
      badgeColor="bg-emerald-50 text-emerald-700 border border-emerald-100"
      footerAction={
        <Link
          to={link}
          className="flex items-center text-sm font-medium text-[#059669] hover:text-[#047857] transition-colors group/link"
        >
          Book Appointment <Calendar className="w-4 h-4 ml-1 transform group-hover/link:translate-x-1 transition-transform" strokeWidth={1.5} />
        </Link>
      }
    >
      <p className="text-gray-600 text-xs leading-relaxed line-clamp-3">
        Connect with certified financial advisors to get personalized investment advice tailored to your goals.
      </p>
    </DashboardCard>
  );
};

// Refer-and-Earn Card Component
const ReferAndEarnCard = ({
  reward = "Earn ₹50",
  image = referImage,
  link = "/referral",
}) => {
  return (
    <DashboardCard
      title="Refer & Earn"
      subtitle={`Get ${reward} per referral`}
      image={image}
      badge="Rewards"
      badgeColor="bg-purple-50 text-purple-700 border border-purple-100"
      footerAction={
        <Link
          to={link}
          className="flex items-center text-sm font-medium text-[#7C3AED] hover:text-[#6D28D9] transition-colors group/link"
        >
          Invite Friends <Gift className="w-4 h-4 ml-1 transform group-hover/link:translate-x-1 transition-transform" strokeWidth={1.5} />
        </Link>
      }
    >
      <p className="text-gray-600 text-xs leading-relaxed line-clamp-3">
        Share the benefits of smart investing with your network and earn rewards for every successful referral.
      </p>
    </DashboardCard>
  );
};

// Popular Companies Card Component
const PopularCompaniesCard = ({ image = popularCompaniesImage }) => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPopularCompanies = async () => {
      try {
        const response = await apiService.getScreenerData();
        const popularCompanies = response.data
          ?.filter(
            (company) =>
              typeof company.basicData?.legalName === "string" &&
              !/^Sat\s/.test(company.basicData.legalName) &&
              company.basicData.legalName.length > 5
          )
          .sort((a, b) => (b.popularityScore || 0) - (a.popularityScore || 0))
          .slice(0, 3)
          .map((company) => ({
            name: company.basicData.legalName,
            uniqueTitle: company.basicData.uniqueTitle,
            logo: company.logo || company.basicData?.logo || null,
          }));

        setCompanies(popularCompanies || []);
      } catch (err) {
        console.error("Error fetching popular companies:", err.message);
        setError("Failed to fetch popular companies.");
      } finally {
        setLoading(false);
      }
    };

    fetchPopularCompanies();
  }, []);

  const handleRedirect = (uniqueTitle) => {
    if (!uniqueTitle) return;
    navigate(`/company_info/${uniqueTitle}`);
  };

  return (
    <DashboardCard
      title="Popular Companies"
      subtitle="Trending in the market"
      image={image}
      badge="Trending"
      badgeColor="bg-amber-50 text-amber-700 border border-amber-100"
      footerAction={
        <Link
          to="/screener"
          className="flex items-center text-sm font-medium text-[#D97706] hover:text-[#B45309] transition-colors group/link"
        >
          View All <ArrowRight className="w-4 h-4 ml-1 transform group-hover/link:translate-x-1 transition-transform" strokeWidth={1.5} />
        </Link>
      }
    >
      {loading ? (
        <div className="space-y-2 animate-pulse">
          <div className="h-8 bg-gray-50 rounded-lg"></div>
          <div className="h-8 bg-gray-50 rounded-lg"></div>
          <div className="h-8 bg-gray-50 rounded-lg"></div>
        </div>
      ) : error ? (
        <div className="flex items-center text-red-500 text-xs bg-red-50 p-2 rounded-lg">
          <AlertCircle className="w-3 h-3 mr-1" strokeWidth={1.5} /> {error}
        </div>
      ) : (
        <div className="space-y-1">
          {companies.map((company, index) => (
            <button
              key={index}
              onClick={() => handleRedirect(company.uniqueTitle)}
              className="flex items-center w-full text-left p-1.5 hover:bg-amber-50 rounded-lg transition-colors group/item"
            >
              <div className="w-6 h-6 rounded-lg overflow-hidden border border-gray-100 bg-amber-100 flex items-center justify-center text-amber-600 mr-2 font-bold text-[10px] flex-shrink-0">
                {company.logo ? (
                  <img
                    src={company.logo}
                    alt={company.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className={company.logo ? "hidden" : "w-full h-full flex items-center justify-center"}>
                  {index + 1}
                </div>
              </div>
              <span className="text-xs font-medium text-gray-700 group-hover/item:text-amber-700 truncate flex-1">
                {company.name}
              </span>
              <ChevronRight className="w-3 h-3 text-gray-300 group-hover/item:text-amber-500" strokeWidth={1.5} />
            </button>
          ))}
        </div>
      )}
    </DashboardCard>
  );
};

const NewlyAddedCompaniesCard = ({ image = newlyAddedCompaniesImage }) => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { triggerRecentSort } = useScreener();

  useEffect(() => {
    const fetchNewlyAddedCompanies = async () => {
      try {
        const response = await apiService.getScreenerData();
        const newCompanies = response.data
          ?.filter(
            (company) =>
              typeof company.basicData?.legalName === "string" &&
              !/^Sat\s/.test(company.basicData.legalName) &&
              company.basicData.legalName.length > 5
          )
          .sort(
            (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
          )
          .slice(0, 3)
          .map((company) => ({
            name: company.basicData.legalName,
            uniqueTitle: company.basicData.uniqueTitle,
            logo: company.logo || company.basicData?.logo || null,
          }));

        setCompanies(newCompanies || []);
      } catch (err) {
        console.error("Error fetching newly added companies:", err.message);
        setError("Failed to fetch new companies.");
      } finally {
        setLoading(false);
      }
    };

    fetchNewlyAddedCompanies();
  }, []);

  const handleRedirect = (uniqueTitle) => {
    if (!uniqueTitle) return;
    navigate(`/company_info/${uniqueTitle}`);
  };

  const handleViewAllClick = () => {
    triggerRecentSort();
    navigate("/screenerlist");
  };

  return (
    <DashboardCard
      title="New Arrivals"
      subtitle="Fresh market entries"
      image={image}
      badge="Just Added"
      badgeColor="bg-teal-50 text-teal-700 border border-teal-100"
      footerAction={
        <button
          onClick={handleViewAllClick}
          className="flex items-center text-sm font-medium text-[#0D9488] hover:text-[#0F766E] transition-colors group/link"
        >
          View All <ArrowRight className="w-4 h-4 ml-1 transform group-hover/link:translate-x-1 transition-transform" strokeWidth={1.5} />
        </button>
      }
    >
      {loading ? (
        <div className="space-y-2 animate-pulse">
          <div className="h-8 bg-gray-50 rounded-lg"></div>
          <div className="h-8 bg-gray-50 rounded-lg"></div>
          <div className="h-8 bg-gray-50 rounded-lg"></div>
        </div>
      ) : error ? (
        <div className="flex items-center text-red-500 text-xs bg-red-50 p-2 rounded-lg">
          <AlertCircle className="w-3 h-3 mr-1" strokeWidth={1.5} /> {error}
        </div>
      ) : (
        <div className="space-y-1">
          {companies.map((company, index) => (
            <button
              key={index}
              onClick={() => handleRedirect(company.uniqueTitle)}
              className="flex items-center w-full text-left p-1.5 hover:bg-teal-50 rounded-lg transition-colors group/item"
            >
              <div className="w-6 h-6 rounded-lg overflow-hidden border border-gray-100 bg-teal-100 flex items-center justify-center text-teal-600 mr-2 flex-shrink-0">
                {company.logo ? (
                  <img
                    src={company.logo}
                    alt={company.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className={company.logo ? "hidden" : "w-full h-full flex items-center justify-center"}>
                  <TrendingUp className="w-3 h-3" strokeWidth={1.5} />
                </div>
              </div>
              <span className="text-xs font-medium text-gray-700 group-hover/item:text-teal-700 truncate flex-1">
                {company.name}
              </span>
              <ChevronRight className="w-3 h-3 text-gray-300 group-hover/item:text-teal-500" strokeWidth={1.5} />
            </button>
          ))}
        </div>
      )}
    </DashboardCard>
  );
};

// Companies with Dividend Yield Card Component
const DividendYieldCompaniesCard = ({ image = dividendYieldImage }) => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDividendCompanies = async () => {
      try {
        const response = await apiService.getScreenerData();
        const dividendCompanies = response.data
          ?.filter(
            (company) =>
              typeof company.basicData?.legalName === "string" &&
              !/^Sat\s/.test(company.basicData.legalName) &&
              company.basicData.legalName.length > 5 &&
              company.financialData?.dividendYield > 0
          )
          .sort(
            (a, b) =>
              (b.financialData?.dividendYield || 0) -
              (a.financialData?.dividendYield || 0)
          )
          .slice(0, 3)
          .map((company) => ({
            name: company.basicData.legalName,
            uniqueTitle: company.basicData.uniqueTitle,
            dividendYield: company.financialData?.dividendYield,
            logo: company.logo || company.basicData?.logo || null,
          }));

        setCompanies(dividendCompanies || []);
      } catch (err) {
        console.error("Error fetching dividend yield companies:", err.message);
        setError("Failed to fetch dividend companies.");
      } finally {
        setLoading(false);
      }
    };

    fetchDividendCompanies();
  }, []);

  const handleRedirect = (uniqueTitle) => {
    if (!uniqueTitle) return;
    navigate(`/company_info/${uniqueTitle}`);
  };

  return (
    <DashboardCard
      title="High Yield"
      subtitle="Top dividend payers"
      image={image}
      badge="Income"
      badgeColor="bg-orange-50 text-orange-700 border border-orange-100"
      footerAction={
        <Link
          to="/screener"
          className="flex items-center text-sm font-medium text-[#EA580C] hover:text-[#C2410C] transition-colors group/link"
        >
          View All <ArrowRight className="w-4 h-4 ml-1 transform group-hover/link:translate-x-1 transition-transform" strokeWidth={1.5} />
        </Link>
      }
    >
      {loading ? (
        <div className="space-y-2 animate-pulse">
          <div className="h-8 bg-gray-50 rounded-lg"></div>
          <div className="h-8 bg-gray-50 rounded-lg"></div>
          <div className="h-8 bg-gray-50 rounded-lg"></div>
        </div>
      ) : error ? (
        <div className="flex items-center text-red-500 text-xs bg-red-50 p-2 rounded-lg">
          <AlertCircle className="w-3 h-3 mr-1" strokeWidth={1.5} /> {error}
        </div>
      ) : (
        <div className="space-y-1">
          {companies.map((company, index) => (
            <button
              key={index}
              onClick={() => handleRedirect(company.uniqueTitle)}
              className="flex items-center justify-between w-full text-left p-1.5 hover:bg-orange-50 rounded-lg transition-colors group/item"
            >
              <div className="flex items-center min-w-0 flex-1 mr-2">
                <div className="w-6 h-6 rounded-lg overflow-hidden border border-gray-100 bg-orange-100 flex items-center justify-center text-orange-600 mr-2 flex-shrink-0">
                  {company.logo ? (
                    <img
                      src={company.logo}
                      alt={company.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className={company.logo ? "hidden" : "w-full h-full flex items-center justify-center"}>
                    <span className="font-bold text-[10px]">{company.name.charAt(0).toUpperCase()}</span>
                  </div>
                </div>
                <span className="text-xs font-medium text-gray-700 group-hover/item:text-orange-700 truncate">
                  {company.name}
                </span>
              </div>
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-orange-100 text-orange-700 text-[10px] font-bold">
                {company.dividendYield.toFixed(2)}%
              </span>
            </button>
          ))}
        </div>
      )}
    </DashboardCard>
  );
};

function OverviewComponent() {
  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-16 bg-gray-50/50">
      <div className="flex items-center mb-8">
        <div className="w-1 h-8 bg-gradient-to-b from-[#44D2F6] to-[#0C7FA5] rounded-full mr-3"></div>
        <div>
          <h1 className="text-2xl font-bold text-[#102147]">
            Investment Dashboard
          </h1>
          <p className="text-gray-500 text-xs mt-0.5">Overview of market insights and opportunities</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        <BlogCard />
        <ExpertsCard />
        <ReferAndEarnCard />
        <PopularCompaniesCard />
        <NewlyAddedCompaniesCard />
        <DividendYieldCompaniesCard />
      </div>
    </div>
  );
}

export default OverviewComponent;
